import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Box, Button, MenuItem, Paper, Select, TextField, Tooltip, Typography,
} from '@mui/material'
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from 'recharts'
import { useSnackbar } from 'notistack'
import RefreshIcon from '@mui/icons-material/Refresh'
import GroupsIcon from '@mui/icons-material/Groups'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import TrackChangesIcon from '@mui/icons-material/TrackChanges'
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee'
import Inventory2Icon from '@mui/icons-material/Inventory2'
import StorefrontIcon from '@mui/icons-material/Storefront'
import PeopleIcon from '@mui/icons-material/People'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import {
  useGetPocDashboardQuery, useTriggerRevenueSyncMutation,
} from '../../redux/growthosPocAnalyticsApiSlice'
import { useListIngestRunsQuery } from '../../redux/growthosConnectorsApiSlice'
import {
  GX_BORDER, GX_INK_500, GX_INK_700, GX_INK_900, GX_CARD_SX, GX_PAGE_SX, GX_COLORS, GX_CHART_PALETTE,
  GxKpiCard, GxPill, GxSyncStatusBar,
} from '../growthos-shared/GrowthOsUi'
import { GxPagedTable } from '../growthos-shared/GxPagedTable'
import {
  comparison, compactCurrency, currency, daysAgoLabel, integer, percentage, shortDate, GrowthPct,
} from '../growthos-shared/gxFormat'
import { PERIODS, ScopeTag } from './pocFormat'

const INK_400 = '#94a3b8'

function StatusBadge({ status }) {
  const map = {
    Won: { c: '#059669', b: 'rgba(5,150,105,0.3)' },
    Lost: { c: '#e11d48', b: 'rgba(225,29,72,0.3)' },
    Open: { c: '#b45309', b: 'rgba(180,83,9,0.3)' },
    WIP: { c: '#b45309', b: 'rgba(180,83,9,0.3)' },
  }
  const s = map[status] || { c: GX_INK_500, b: GX_BORDER }
  // "Open" for the undecided status — "WIP" is reserved for the Hot/Warm open-deal metrics.
  return (
    <Box component="span" sx={{ display: 'inline-block', px: 1, py: 0.25, borderRadius: '9999px', border: `1px solid ${s.b}`, color: s.c, fontSize: 11, fontWeight: 600 }}>
      {status === 'WIP' ? 'Open' : status}
    </Box>
  )
}

function SectionCard({ icon, iconColor, title, subtitle, right, children }) {
  return (
    <Paper variant="outlined" sx={{ ...GX_CARD_SX, mb: 3, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', px: 2.5, pt: 2.5, pb: 1 }}>
        {icon && <Box sx={{ color: iconColor, display: 'flex' }}>{icon}</Box>}
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: GX_INK_700 }}>{title}</Typography>
          {subtitle && <Typography sx={{ fontSize: 12, color: INK_400 }}>{subtitle}</Typography>}
        </Box>
        {right}
      </Box>
      <Box sx={{ px: 2.5, pb: 2.5 }}>{children}</Box>
    </Paper>
  )
}

const groupHeadSx = { color: GX_COLORS.indigo, borderBottom: `2px solid ${GX_COLORS.indigo}33` }
const groupHeadAltSx = { color: GX_COLORS.teal, borderBottom: `2px solid ${GX_COLORS.teal}33` }

function PocDrill({ row, timeframe }) {
  return (
    <Box sx={{ p: 2, bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Tooltip title="Visible brands only — hidden-from-display brands stay in the row's counts but are never listed by name.">
          <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: INK_400, mb: 1, display: 'inline-block' }}>
            Brands served ({row.brands.length})
          </Typography>
        </Tooltip>
        <GxPagedTable variant="poc"
          rows={row.brands}
          rowKey={(b) => b.brandId}
          emptyText="No shipping brands in this window."
          headerGroups={[
            { label: timeframe ? `Period · ${timeframe}` : '', span: 1, align: 'left', sx: { textTransform: 'none', fontWeight: 500 } },
            { label: 'Shipments', span: 3, sx: groupHeadSx },
            { label: 'Revenue', span: 3, sx: groupHeadAltSx },
            { label: '', span: 1 },
          ]}
          columns={[
            { key: 'brand', header: 'Brand', title: 'Brand name (ProShip / Data → Brands).', sortValue: (b) => b.brandName, cell: (b) => <Typography component="span" sx={{ fontWeight: 600, fontSize: 12.5 }}>{b.brandName}</Typography> },
            { key: 'prevShip', header: 'Comparable', align: 'right', title: 'The equal-length window immediately before the selected one.', sortValue: (b) => b.prevShipments, cell: (b) => integer(b.prevShipments) },
            { key: 'ship', header: 'Current', align: 'right', title: 'The selected window (follows the period filter).', sortValue: (b) => b.shipments, cell: (b) => integer(b.shipments) },
            { key: 'shipPct', header: 'Δ %', align: 'right', title: '% change: (Current − Comparable) ÷ Comparable.', sortValue: (b) => b.shipmentChangePct, cell: (b) => <GrowthPct pct={b.shipmentChangePct} /> },
            { key: 'prevRev', header: 'Comparable', align: 'right', title: 'The equal-length window immediately before the selected one.', sortValue: (b) => b.prevRevenue, cell: (b) => currency(b.prevRevenue) },
            { key: 'rev', header: 'Current', align: 'right', title: 'The selected window (follows the period filter).', sortValue: (b) => b.revenue, cell: (b) => currency(b.revenue) },
            { key: 'revPct', header: 'Δ %', align: 'right', title: '% change: (Current − Comparable) ÷ Comparable.', sortValue: (b) => b.revenueChangePct, cell: (b) => <GrowthPct pct={b.revenueChangePct} /> },
            { key: 'lastShip', header: 'Last shipment', align: 'right', title: "The brand's most recent positive shipment, measured against the ProShip data-through date.", sortValue: (b) => (b.daysSinceShipment == null ? -1 : b.daysSinceShipment), cell: (b) => daysAgoLabel(b.daysSinceShipment) },
          ]}
        />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: INK_400, mb: 1 }}>
          Deals created in window ({row.deals.length})
        </Typography>
        <GxPagedTable variant="poc"
          rows={row.deals}
          rowKey={(d) => d.dealId}
          emptyText="No deals created in this window."
          columns={[
            { key: 'deal', header: 'Deal / Brand', title: 'HubSpot deal, labelled by its linked brand.', sortValue: (d) => d.brandName, cell: (d) => <Typography component="span" sx={{ fontWeight: 600, fontSize: 12.5 }}>{d.brandName}</Typography> },
            { key: 'status', header: 'Status', title: 'Deal outcome: Won, Lost, or Open (undecided).', sortValue: (d) => d.status, cell: (d) => <StatusBadge status={d.status} /> },
            { key: 'stage', header: 'Stage', title: 'Current HubSpot pipeline stage.', sortValue: (d) => d.stage || '', cell: (d) => d.stage || '—' },
            { key: 'created', header: 'Created', title: 'Deal create date (HubSpot).', sortValue: (d) => d.createdDate || '', cell: (d) => d.createdDate || '—' },
            { key: 'shipPot', header: 'Shipment Potential', align: 'right', title: 'Expected monthly shipments (orders/month) on the deal.', sortValue: (d) => d.shipmentPotential, cell: (d) => integer(d.shipmentPotential) },
            { key: 'mbr', header: 'Est. MBR', align: 'right', title: 'Estimated Monthly Billing Revenue (₹/month) on the deal.', sortValue: (d) => d.estimatedMbr, cell: (d) => currency(d.estimatedMbr) },
          ]}
        />
      </Box>
    </Box>
  )
}

export default function SalesPocDashboardPage() {
  const { enqueueSnackbar } = useSnackbar()
  const [period, setPeriod] = useState('mtd')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [openPoc, setOpenPoc] = useState(null)

  const customReady = period !== 'custom' || Boolean(customFrom && customTo)
  const { data, isLoading, isFetching, error } = useGetPocDashboardQuery(
    { period, from: period === 'custom' ? customFrom : undefined, to: period === 'custom' ? customTo : undefined },
    { skip: !customReady },
  )
  const { data: runsData } = useListIngestRunsQuery()
  const [triggerSync, syncState] = useTriggerRevenueSyncMutation()
  const proshipLabel = data?.coverage?.proship?.label
  const hubspotLabel = data?.coverage?.hubspot?.label

  const chartData = useMemo(
    () => (data?.leaderboard || []).slice(0, 10).map((r) => ({ name: r.owner, revenue: r.revenue, shipments: r.shipments })),
    [data],
  )
  const openRow = openPoc ? (data?.leaderboard || []).find((x) => x.owner === openPoc) : null

  const handleSync = async () => {
    try {
      await triggerSync().unwrap()
      enqueueSnackbar('Revenue data refreshed.', { variant: 'success' })
    } catch (e) {
      enqueueSnackbar(e?.data?.message || e?.message || 'Sync failed.', { variant: 'error' })
    }
  }

  return (
    <Box sx={GX_PAGE_SX}>
      <GxSyncStatusBar runs={runsData?.runs || []} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box>
          <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', color: GX_COLORS.indigo, mb: 1 }}>
            <PeopleIcon sx={{ fontSize: 14 }} /> Performance by Sales Point of Contact
          </Typography>
          <Typography sx={{ fontSize: 24, fontWeight: 700, color: GX_INK_900 }}>Sales POCs</Typography>
          <Typography sx={{ fontSize: 13, color: GX_INK_500, mt: 0.5, maxWidth: 720 }}>
            Revenue &amp; shipments are ProShip actuals (capped at ProShip&rsquo;s data-through date, marketing-onboarded
            merchants); deals are marketing-sourced HubSpot deals across all pipelines.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Select size="small" value={period} onChange={(e) => { setPeriod(e.target.value); setOpenPoc(null) }} sx={{ minWidth: 140, fontSize: 13, bgcolor: '#fff' }}>
            {PERIODS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
          </Select>
          {period === 'custom' && (
            <>
              <TextField size="small" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
              <Typography sx={{ color: GX_INK_500 }}>→</Typography>
              <TextField size="small" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
            </>
          )}
          {data?.window && (
            <GxPill color={GX_COLORS.indigo} icon={<CalendarTodayIcon sx={{ fontSize: 12 }} />}>
              Showing: {data.window.label} · {shortDate(data.window.from)} – {shortDate(data.window.to)}
            </GxPill>
          )}
          <Tooltip title="Re-roll the last few days of real order data into this dashboard">
            <Button size="small" variant="outlined" startIcon={<RefreshIcon />} disabled={syncState.isLoading} onClick={handleSync} sx={{ textTransform: 'none', borderRadius: '10px' }}>
              Refresh data
            </Button>
          </Tooltip>
          <Link to="/growthos/pocs" style={{ fontSize: 13, color: '#4338ca', textDecoration: 'none', whiteSpace: 'nowrap' }}>
            Manage POCs →
          </Link>
        </Box>
      </Box>

      {error && (
        <Box sx={{ mb: 2, p: 2, borderRadius: '16px', border: '1px solid #fecdd3', bgcolor: '#fff1f2', color: '#be123c', fontSize: 13 }}>
          {error?.data?.message || error?.message || `HTTP ${error?.status}`}
        </Box>
      )}
      {period === 'custom' && !customReady && (
        <Box sx={{ mb: 2, p: 2, borderRadius: '16px', border: '1px solid #fde68a', bgcolor: '#fffbeb', color: '#b45309', fontSize: 13 }}>
          Pick a start and end date to load the custom range.
        </Box>
      )}
      {data && !data.hasData && (
        <Box sx={{ mb: 2, p: 2, borderRadius: '16px', border: '1px solid #fde68a', bgcolor: '#fffbeb', color: '#b45309', fontSize: 13 }}>
          No POC activity in this window.
        </Box>
      )}

      {/* KPI tiles */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <GxKpiCard label="Total Revenue" icon={<CurrencyRupeeIcon sx={{ fontSize: 16 }} />} iconColor={GX_COLORS.indigo}
          value={data ? currency(data.summary.totalRevenue.value) : (isLoading ? '…' : '—')}
          sub={data ? comparison(data.summary.totalRevenue, currency) : undefined} />
        <GxKpiCard label="Total Shipments" icon={<Inventory2Icon sx={{ fontSize: 16 }} />} iconColor={GX_COLORS.teal}
          value={data ? integer(data.summary.totalShipments.value) : (isLoading ? '…' : '—')}
          sub={data ? comparison(data.summary.totalShipments, integer) : undefined} />
        <GxKpiCard label="Active Brands" icon={<StorefrontIcon sx={{ fontSize: 16 }} />} iconColor={GX_COLORS.amber}
          value={data ? integer(data.summary.activeBrands) : (isLoading ? '…' : '—')} sub="Distinct brands shipping in window" />
        <GxKpiCard label="Active POCs" icon={<PeopleIcon sx={{ fontSize: 16 }} />} iconColor={GX_COLORS.pink}
          value={data ? integer(data.summary.activePocs) : (isLoading ? '…' : '—')} sub="POCs with shipments or deals" />
      </Box>

      {/* Revenue by POC chart */}
      <Paper variant="outlined" sx={{ ...GX_CARD_SX, p: 2.5, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <CurrencyRupeeIcon sx={{ fontSize: 16, color: GX_INK_700 }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: GX_INK_700 }}>Revenue by Sales POC</Typography>
          {proshipLabel && <ScopeTag label={`ProShip: ${proshipLabel}`} title="Revenue/shipments use the actual ProShip data-through date." />}
        </Box>
        <Box sx={{ mt: 2, height: Math.max(240, chartData.length * 42) }}>
          {chartData.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef0f6" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: GX_INK_500 }} tickFormatter={(v) => compactCurrency(v)} />
                <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11, fill: GX_INK_700 }} />
                <RTooltip formatter={(v) => currency(v)} />
                <Bar dataKey="revenue" name="Revenue" radius={[0, 4, 4, 0]}>
                  {chartData.map((_, i) => <Cell key={i} fill={GX_CHART_PALETTE[i % GX_CHART_PALETTE.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography sx={{ fontSize: 13, color: INK_400 }}>{isFetching ? 'Loading…' : 'No revenue in this window.'}</Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* POC Leaderboard */}
      <SectionCard
        icon={<EmojiEventsIcon sx={{ fontSize: 18 }} />} iconColor={GX_COLORS.amber}
        title="POC Leaderboard"
        subtitle="Ranked by revenue · click the row to expand brands & deals, or click a name to open their page"
        right={hubspotLabel ? <ScopeTag label={`HubSpot: ${hubspotLabel}`} title="Deal counts / win rate use the HubSpot window (capped at the last completed sync)." /> : null}
      >
        <GxPagedTable variant="poc"
          rows={data?.leaderboard || []}
          rowKey={(r) => r.owner}
          filter={(r, q) => `${r.owner} ${r.team || ''}`.toLowerCase().includes(q)}
          filterPlaceholder="Filter Sales POC…"
          emptyText={isFetching ? 'Loading…' : 'No Sales POC activity in this window.'}
          onRowClick={(r) => setOpenPoc((o) => (o === r.owner ? null : r.owner))}
          isRowActive={(r) => r.owner === openPoc}
          columns={[
            {
              key: 'poc', header: 'Sales POC', title: 'Deal owner (Sales POC). Click the row to expand brands & deals; click the name to open their page.', sortValue: (r) => r.owner,
              cell: (r) => (r.personId
                ? <Link to={`/growthos/pocs-analytics/${r.personId}`} onClick={(e) => e.stopPropagation()} style={{ color: '#4338ca', fontWeight: 600, textDecoration: 'none' }}>{r.owner}</Link>
                : <Typography component="span" sx={{ fontWeight: 600, fontSize: 12.5 }}>{r.owner}</Typography>),
            },
            { key: 'revenue', header: 'Revenue', align: 'right', title: 'ProShip revenue (₹) in the selected window.', sortValue: (r) => r.revenue, cell: (r) => currency(r.revenue) },
            { key: 'shipments', header: 'Shipments', align: 'right', title: 'ProShip shipments in the selected window.', sortValue: (r) => r.shipments, cell: (r) => integer(r.shipments) },
            { key: 'activeBrands', header: 'Active Brands', align: 'right', title: 'Distinct brands the POC shipped (≥1 positive shipment) in the window.', sortValue: (r) => r.activeBrands, cell: (r) => integer(r.activeBrands) },
            { key: 'growth', header: 'Growth vs Prior', align: 'right', title: 'Revenue vs the comparable prior period (equal length).', sortValue: (r) => (r.momGrowthPct == null ? -Infinity : r.momGrowthPct), cell: (r) => <GrowthPct pct={r.momGrowthPct} /> },
            { key: 'openPipeline', header: 'Open Pipeline', align: 'right', title: "Σ Est. MBR (₹/month) of the POC's currently-open deals, any create date — live snapshot.", sortValue: (r) => r.openPipelineMbr, cell: (r) => compactCurrency(r.openPipelineMbr) },
            { key: 'winRate', header: 'Win Rate', align: 'right', title: 'Deals Won ÷ decided deals (Won + Lost), closed in window.', sortValue: (r) => (r.winRate == null ? -1 : r.winRate), cell: (r) => percentage(r.winRate) },
          ]}
        />
        {openRow && (
          <Box sx={{ mt: 1.5, border: `1px solid ${GX_BORDER}`, borderRadius: '12px', overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 1.5, bgcolor: '#f8fafc' }}>
              <GroupsIcon sx={{ fontSize: 16, color: GX_COLORS.indigo }} />
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: GX_INK_700 }}>{openRow.owner}</Typography>
            </Box>
            <PocDrill row={openRow} timeframe={proshipLabel} />
          </Box>
        )}
      </SectionCard>

      {/* Opportunity Achievement */}
      <SectionCard
        icon={<TrackChangesIcon sx={{ fontSize: 18 }} />} iconColor={GX_COLORS.indigo}
        title="Opportunity Achievement"
        subtitle="Deal outcomes per Sales POC · Deals Got = created in window; Deals Won = closed-won in window (any create date); Win Rate = Won ÷ decided (Won + Lost)"
      >
        <GxPagedTable variant="poc"
          rows={data?.opportunity || []}
          rowKey={(r) => r.owner}
          filter={(r, q) => `${r.owner} ${r.team || ''}`.toLowerCase().includes(q)}
          filterPlaceholder="Filter Sales POC…"
          emptyText={isFetching ? 'Loading…' : 'No deal activity in this window.'}
          columns={[
            { key: 'poc', header: 'Sales POC', title: 'Deal owner (Sales POC).', sortValue: (r) => r.owner, cell: (r) => <Typography component="span" sx={{ fontWeight: 600, fontSize: 12.5 }}>{r.owner}</Typography> },
            { key: 'got', header: 'Deals Got', align: 'right', title: 'Deals created in the window.', sortValue: (r) => r.dealsGot, cell: (r) => integer(r.dealsGot) },
            { key: 'won', header: 'Deals Won', align: 'right', title: 'Deals closed-won in the window (any create date) — can exceed Deals Got.', sortValue: (r) => r.dealsWon, cell: (r) => integer(r.dealsWon) },
            { key: 'winRate', header: 'Win Rate', align: 'right', title: 'Deals Won ÷ decided deals (Won + Lost), closed in window — NOT Won ÷ Got.', sortValue: (r) => (r.winRate == null ? -1 : r.winRate), cell: (r) => percentage(r.winRate) },
            { key: 'assigned', header: 'Assigned Shipments', align: 'right', title: 'Σ shipment potential of deals created in the window.', sortValue: (r) => r.assignedShipments, cell: (r) => integer(r.assignedShipments) },
            { key: 'achieved', header: 'Achieved Shipments', align: 'right', title: "ProShip shipments of the POC's assigned brands in the window.", sortValue: (r) => r.achievedShipments, cell: (r) => integer(r.achievedShipments) },
            { key: 'shipAch', header: 'Shipment Achievement', align: 'right', title: 'Achieved ÷ Assigned — can exceed 100% (older deals shipping now).', sortValue: (r) => (r.shipmentAchievement == null ? -1 : r.shipmentAchievement), cell: (r) => percentage(r.shipmentAchievement) },
          ]}
        />
      </SectionCard>
    </Box>
  )
}
